import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, XCircle, Download, Loader2 } from "lucide-react";
import api from "../lib/api";
import { fmtTRY, fmtDate, fmtQty4  } from "../lib/format";
import { errText } from "../lib/errors";
import { toast } from "sonner";

const STATUS_BANNER = {
  taslak: ["bg-stone-100 text-stone-700", "Bu teklif henüz taslak durumda."],
  gonderildi: ["bg-blue-50 text-blue-800", "Teklifiniz hazır — inceleyip yanıtlayabilirsiniz."],
  goruntulendi: ["bg-amber-50 text-amber-800", "Teklifiniz hazır — inceleyip yanıtlayabilirsiniz."],
  kabul: ["bg-green-50 text-green-800", "Teklifi kabul ettiniz. Ekibimiz siparişiniz için sizinle iletişime geçecek."],
  red: ["bg-red-50 text-red-700", "Teklifi reddettiniz."],
  suresi_doldu: ["bg-stone-200 text-stone-600", "Bu teklifin geçerlilik süresi dolmuştur."],
};

export default function QuotePage() {
  const { token } = useParams();
  const [q, setQ] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/teklif/${token}`).then(({ data }) => setQ(data)).catch(() => setNotFound(true));
  }, [token]);

  const respond = async (action) => {
    if (!window.confirm(action === "accept" ? "Teklifi kabul ediyor musunuz?" : "Teklifi reddediyor musunuz?")) return;
    setBusy(true);
    try {
      await api.post(`/teklif/${token}/respond`, { action });
      toast.success(action === "accept" ? "Teklif kabul edildi" : "Teklif reddedildi");
      const { data } = await api.get(`/teklif/${token}`);
      setQ(data);
    } catch (err) { toast.error(errText(err) || "İşlem başarısız"); }
    finally { setBusy(false); }
  };

  if (notFound) return <div className="min-h-screen flex items-center justify-center bg-cream"><p className="font-serif text-2xl text-stone-700">Teklif bulunamadı</p></div>;
  if (!q) return <div className="min-h-screen flex items-center justify-center bg-cream"><Loader2 className="w-8 h-8 animate-spin text-forest" /></div>;

  const [bannerCls, bannerText] = STATUS_BANNER[q.status] || STATUS_BANNER.taslak;
  const canRespond = ["gonderildi", "goruntulendi"].includes(q.status);

  return (
    <div data-testid="quote-public-page" className="min-h-screen bg-cream py-10 px-4">
      <div className="max-w-3xl mx-auto bg-white rounded-3xl border border-stone-200 shadow-sm overflow-hidden">
        <div className="bg-forest text-cream px-8 py-6 flex items-center justify-between">
          <div>
            <p className="font-serif text-xl font-bold">Ata Angora Doğal Gıdalar</p>
            <p className="text-cream/70 text-xs mt-1">Fiyat Teklifi</p>
          </div>
          <p className="font-mono font-bold">{q.quote_number}</p>
        </div>
        <div className={`px-8 py-3 text-sm font-medium ${bannerCls}`} data-testid="quote-status-banner">{bannerText}</div>
        <div className="p-8">
          <div className="flex flex-wrap justify-between gap-4 text-sm">
            <div>
              <p className="text-xs uppercase tracking-wider text-stone-400 font-semibold">Müşteri</p>
              <p className="font-semibold text-stone-900 mt-1">{q.customer.company || q.customer.name}</p>
              {q.customer.company && <p className="text-stone-600">{q.customer.name}</p>}
            </div>
            <div className="text-right">
              <p className="text-stone-500">Tarih: {fmtDate(q.created_at)}</p>
              <p className="text-stone-500">Geçerlilik: <b className="text-stone-800">{q.valid_until || "—"}</b></p>
            </div>
          </div>
          <table className="w-full text-sm mt-6">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-stone-400 border-b border-stone-200"><th className="py-2">Ürün</th><th className="py-2 text-center">Adet</th><th className="py-2 text-right">Birim</th><th className="py-2 text-right">Tutar</th></tr></thead>
            <tbody>
              {q.items.map((i, idx) => (
                <tr key={idx} className="border-b border-stone-100">
                  <td className="py-2.5">{i.name}{i.sku ? <span className="text-xs text-stone-400 ml-2">{i.sku}</span> : null}</td>
                  <td className="py-2.5 text-center">{fmtQty4(i.quantity, i.unit || "")}</td>
                  <td className="py-2.5 text-right">{fmtTRY(i.unit_price)}</td>
                  <td className="py-2.5 text-right font-medium">{fmtTRY(i.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-5 ml-auto max-w-xs space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-stone-500">Ara Toplam (KDV hariç)</span><span>{fmtTRY(q.subtotal)}</span></div>
            {q.discount_total > 0 && <div className="flex justify-between text-terra"><span>İskonto (%{q.discount_rate})</span><span>-{fmtTRY(q.discount_total)}</span></div>}
            <div className="flex justify-between"><span className="text-stone-500">KDV</span><span>{fmtTRY(q.vat_total)}</span></div>
            <div className="flex justify-between font-bold text-lg border-t border-stone-200 pt-2"><span>Genel Toplam</span><span data-testid="quote-total" className="text-forest">{fmtTRY(q.total)}</span></div>
          </div>
          {q.template?.payment_terms && <div className="mt-6 text-sm"><p className="text-xs uppercase tracking-wider text-stone-400 font-semibold">Ödeme Koşulları</p><p className="text-stone-700 mt-1">{q.template.payment_terms}</p></div>}
          {(q.template?.intro_text || q.note) && <div className="mt-4 text-sm"><p className="text-xs uppercase tracking-wider text-stone-400 font-semibold">Açıklama</p><p className="text-stone-700 mt-1">{q.template?.intro_text || q.note}</p></div>}
          <div className="flex flex-wrap gap-3 mt-8">
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/teklif/${token}/pdf`} data-testid="quote-pdf-download" className="flex items-center gap-2 bg-stone-900 hover:bg-stone-800 text-white text-sm font-semibold px-5 py-3 rounded-xl transition-colors"><Download className="w-4 h-4" /> PDF İndir</a>
            {canRespond && (
              <>
                <button data-testid="quote-accept" disabled={busy} onClick={() => respond("accept")} className="flex items-center gap-2 bg-forest hover:bg-forest-dark text-white text-sm font-semibold px-5 py-3 rounded-xl transition-colors"><CheckCircle2 className="w-4 h-4" /> Teklifi Kabul Et</button>
                <button data-testid="quote-reject" disabled={busy} onClick={() => respond("reject")} className="flex items-center gap-2 border border-red-300 text-terra text-sm font-semibold px-5 py-3 rounded-xl hover:bg-red-50 transition-colors"><XCircle className="w-4 h-4" /> Reddet</button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
